import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

export type StatusCategory = 'New' | 'Indeterminate' | 'Done';
export type StatusCategoryKey = 'new' | 'indeterminate' | 'done';
export type OrderByField = 'created' | 'updated' | 'priority';

// JQL takes the capitalised category, a status carries the lowercase key.
export const STATUS_CATEGORY_KEYS: Record<StatusCategory, StatusCategoryKey> = {
  New: 'new',
  Indeterminate: 'indeterminate',
  Done: 'done',
};

export interface AdminIssuesFilters {
  serviceDeskId: number;
  // Jira status ids, not names: Jira returns translated status names but only matches
  // the untranslated ones in JQL, so a name-based filter finds nothing.
  status?: string[];
  statusCategory?: StatusCategory[];
  requestType?: string[];
  sector?: string[];
  store?: string[];
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  reporterAccountId?: string[];
  assigneeAccountId?: string[];
  search?: string;
  pageToken?: string;
  orderBy?: OrderByField;
  order?: 'asc' | 'desc';
}

export interface AdminIssue {
  issueKey: string;
  summary: string;
  status: string;
  statusId: string;
  statusCategory: StatusCategoryKey;
  requestType: string | null;
  priority: string | null;
  reporter: { accountId: string; displayName: string } | null;
  assignee: { accountId: string; displayName: string } | null;
  // Label of the intervention point (store), when the field carries one itself.
  store: string | null;
  // Assets id of that store; `/store-labels` turns it into a name, after the listing.
  storeObjectId: string | null;
  created: string;
  updated: string;
  // Raw ISO date the desk's own Jira field carries: `duedate` on LUXE, `desired_intervention_date`
  // on DPGP, `intervention_datetime` on Flagship. Null when the ticket has none.
  interventionDate: string | null;
  // 'HH:mm' read off the desk's time field; null when unmapped or empty.
  interventionTime: string | null;
  // True when the current user may validate this issue at its current status.
  canValidate: boolean;
}

export interface AdminIssueTransition {
  id: string;
  name: string;
}

export interface AdminIssuesResult {
  issues: AdminIssue[];
  nextPageToken: string | null;
  // Approximate total issue count, present only on the first page.
  total: number | null;
}

const buildQueryString = (filters: AdminIssuesFilters): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.set(key, String(value));
    }
  });
  return params.toString();
};

export const fetchAdminIssues = async (filters: AdminIssuesFilters): Promise<AdminIssuesResult> => {
  const qs = buildQueryString(filters);
  const res = await fetchWithAuth(`admin/issues?${qs}`);
  return unwrap<AdminIssuesResult>(res, 'Failed to fetch admin issues');
};

// The store names of one listed page. Deliberately not part of the listing: Assets takes
// ~0.5s to answer and the tickets must not wait for it.
export const fetchAdminIssueStoreLabels = async (
  serviceDeskId: number,
  ids: string[]
): Promise<Record<string, string>> => {
  const params = new URLSearchParams({ serviceDeskId: String(serviceDeskId) });
  ids.forEach((id) => params.append('ids', id));
  const res = await fetchWithAuth(`admin/issues/store-labels?${params.toString()}`);
  return unwrap<Record<string, string>>(res, 'Failed to fetch store labels');
};

export interface AdminIssuesStoreOption {
  label: string;
  sector: string | null;
}

export interface AdminIssuesStatusOption {
  id: string;
  name: string;
  category: StatusCategoryKey;
}

// What `/filter-options` returns: the two lists that need no Assets scan.
export interface AdminIssuesStatusAndTypeOptions {
  statuses: AdminIssuesStatusOption[];
  requestTypes: { id: string; name: string }[];
}

// What `/store-options` returns, behind a desk-wide Assets scan that takes seconds.
export interface AdminIssuesStoreOptions {
  stores: AdminIssuesStoreOption[];
  sectors: string[];
}

// The two joined back together, which is all any consumer of the filters ever sees.
export type AdminIssuesFilterOptions = AdminIssuesStatusAndTypeOptions & AdminIssuesStoreOptions;

export interface IssueUserOption {
  accountId: string;
  displayName: string;
}

export const fetchAdminIssuesFilterOptions = async (
  serviceDeskId: number
): Promise<AdminIssuesStatusAndTypeOptions> => {
  const res = await fetchWithAuth(`admin/issues/filter-options?serviceDeskId=${serviceDeskId}`);
  return unwrap<AdminIssuesStatusAndTypeOptions>(res, 'Failed to fetch filter options');
};

export const fetchAdminIssuesStoreOptions = async (
  serviceDeskId: number
): Promise<AdminIssuesStoreOptions> => {
  const res = await fetchWithAuth(`admin/issues/store-options?serviceDeskId=${serviceDeskId}`);
  return unwrap<AdminIssuesStoreOptions>(res, 'Failed to fetch store options');
};

export const searchAdminIssuesUsers = async (
  serviceDeskId: number,
  q: string
): Promise<IssueUserOption[]> => {
  const res = await fetchWithAuth(
    `admin/issues/users?serviceDeskId=${serviceDeskId}&q=${encodeURIComponent(q)}`
  );
  return unwrap<IssueUserOption[]>(res, 'Failed to search users');
};

export const fetchAdminIssueTransitions = async (
  issueKey: string
): Promise<AdminIssueTransition[]> => {
  const res = await fetchWithAuth(`admin/issues/${encodeURIComponent(issueKey)}/transitions`);
  return unwrap<AdminIssueTransition[]>(res, 'Failed to fetch transitions');
};

export const transitionAdminIssue = async (
  issueKey: string,
  transitionId: string
): Promise<void> => {
  const res = await fetchWithAuth(
    `admin/issues/${encodeURIComponent(issueKey)}/transitions/${encodeURIComponent(transitionId)}`,
    { method: 'POST' }
  );
  return unwrap<void>(res, 'Failed to transition issue');
};
